import L from 'leaflet'
import { computeMeasurement, formatMeasurement } from './measure.js'
import { escapeHtml } from '../lib/escape.js'

export const DEFAULT_STYLE = {
  color: '#3b82f6',
  weight: 3,
  fillColor: '#3b82f6',
  fillOpacity: 0.2,
}

// Geoman shape name -> our feature kind.
const SHAPE_TO_KIND = {
  Marker: 'marker',
  Line: 'line',
  Polygon: 'polygon',
  Rectangle: 'rectangle',
  Circle: 'circle',
}

export function shapeToKind(shape) {
  return SHAPE_TO_KIND[shape] || null
}

// Extract a GeoJSON geometry from a Leaflet layer, plus a radius for circles
// (GeoJSON has no circle type, so we carry the radius in style).
export function geometryFromLayer(layer, kind) {
  const geometry = layer.toGeoJSON().geometry
  const extra = {}
  if (kind === 'circle' && typeof layer.getRadius === 'function') {
    extra.radius = layer.getRadius() // metres
  }
  return { geometry, extra }
}

// Colored pin icon so each author's markers carry their color.
export function coloredMarkerIcon(color) {
  const svg = `
    <svg width="26" height="38" viewBox="0 0 26 38" xmlns="http://www.w3.org/2000/svg">
      <path d="M13 0C5.82 0 0 5.82 0 13c0 9.75 13 25 13 25s13-15.25 13-25C26 5.82 20.18 0 13 0z"
            fill="${color}" stroke="#ffffff" stroke-width="2"/>
      <circle cx="13" cy="13" r="5" fill="#ffffff"/>
    </svg>`
  return L.divIcon({
    className: 'collab-marker',
    html: svg,
    iconSize: [26, 38],
    iconAnchor: [13, 38],
    popupAnchor: [0, -34],
    // Float the (permanent) label just above the pin head.
    tooltipAnchor: [0, -36],
  })
}

// Build the Leaflet layer for a stored feature object. extraOpts is merged into
// the layer options — used to create OTHER users' features with pmIgnore: true
// (not editable/draggable/removable via Geoman) while keeping snapIgnore: false
// (still valid snap targets).
export function featureToLayer(feature, extraOpts = {}) {
  const { kind, geojson, style } = feature
  const s = { ...DEFAULT_STYLE, ...(style || {}) }
  switch (kind) {
    case 'marker': {
      const [lng, lat] = geojson.coordinates
      return L.marker([lat, lng], { icon: coloredMarkerIcon(s.color), ...extraOpts })
    }
    case 'line': {
      const latlngs = geojson.coordinates.map(([lng, lat]) => [lat, lng])
      return L.polyline(latlngs, { ...pathStyle(s), ...extraOpts })
    }
    case 'polygon':
    case 'rectangle': {
      // Rectangles are stored and rebuilt as polygons — visually identical and
      // editable as vertices. Outer ring only (no holes in v1).
      const latlngs = geojson.coordinates[0].map(([lng, lat]) => [lat, lng])
      return L.polygon(latlngs, { ...pathStyle(s), ...extraOpts })
    }
    case 'circle': {
      const [lng, lat] = geojson.coordinates
      return L.circle([lat, lng], { ...pathStyle(s), radius: s.radius || 100, ...extraOpts })
    }
    default:
      return null
  }
}

function pathStyle(s) {
  return {
    color: s.color,
    weight: s.weight,
    fillColor: s.fillColor || s.color,
    fillOpacity: s.fillOpacity,
  }
}

// Apply style + label + measurement tooltip to an existing layer.
export function applyPresentation(layer, feature) {
  const s = { ...DEFAULT_STYLE, ...(feature.style || {}) }
  if (feature.kind === 'marker') {
    if (typeof layer.setIcon === 'function') layer.setIcon(coloredMarkerIcon(s.color))
  } else if (typeof layer.setStyle === 'function') {
    layer.setStyle(pathStyle(s))
  }

  const measurementText = formatMeasurement(feature.measurement)
  const label = (feature.label || '').trim()
  // Labels are user input and Leaflet renders tooltip content as HTML — escape
  // to prevent stored XSS. measurementText is app-generated and safe.
  const tipParts = [label && escapeHtml(label), measurementText].filter(Boolean)
  // Always unbind first: rebinding without unbinding leaks a tooltip instance on
  // every update (concurrent edits can fire many per second).
  if (layer.getTooltip()) layer.unbindTooltip()
  if (tipParts.length) {
    // Permanent so labels float next to every feature, pins included.
    layer.bindTooltip(tipParts.join(' · '), {
      permanent: true,
      direction: 'top',
      className: 'feature-tooltip',
    })
  }
}

// Update an existing layer's geometry in place (used when a remote edit arrives).
export function updateLayerGeometry(layer, feature) {
  const { kind, geojson, style } = feature
  switch (kind) {
    case 'marker':
      layer.setLatLng([geojson.coordinates[1], geojson.coordinates[0]])
      break
    case 'line':
      layer.setLatLngs(geojson.coordinates.map(([lng, lat]) => [lat, lng]))
      break
    case 'polygon':
    case 'rectangle':
      layer.setLatLngs(geojson.coordinates[0].map(([lng, lat]) => [lat, lng]))
      break
    case 'circle':
      layer.setLatLng([geojson.coordinates[1], geojson.coordinates[0]])
      // Fall back to the layer's current radius if a remote update omits it,
      // rather than leaving a stale value.
      layer.setRadius(style?.radius || layer.getRadius())
      break
    default:
      break
  }
}

export { computeMeasurement }

// id <-> Leaflet layer registry. Tags each layer with its feature id so Geoman
// events can resolve back to the feature.
export class FeatureRegistry {
  constructor() {
    this.byId = new Map()
  }
  set(id, layer) {
    layer._featureId = id
    this.byId.set(id, layer)
  }
  get(id) {
    return this.byId.get(id)
  }
  idOf(layer) {
    return layer && layer._featureId
  }
  has(id) {
    return this.byId.has(id)
  }
  delete(id) {
    this.byId.delete(id)
  }
  values() {
    return this.byId.values()
  }
}
